import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeguridadTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, security } = userData;
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRevokeAllSessions = async () => {
    if (!window.confirm('¿Revocar todas las sesiones activas? El usuario deberá iniciar sesión nuevamente.')) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/revoke-sessions`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Todas las sesiones han sido revocadas');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al revocar sesiones');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggle2FA = async () => {
    const action = security?.two_fa_enabled ? 'deshabilitar' : 'habilitar';
    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} 2FA para este usuario?`)) return;

    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/toggle-2fa`,
        { enabled: !security?.two_fa_enabled },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert(`2FA ${action}do correctamente`);
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || `Error al ${action} 2FA`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetLoginAttempts = async () => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/reset-login-attempts`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Intentos de login reseteados');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al resetear intentos');
    } finally {
      setIsProcessing(false);
    }
  };

  const activeSessions = security?.active_sessions || [];
  const failedAttempts = security?.failed_login_attempts || 0;
  const isLocked = security?.account_locked || false;

  return (
    <div className="space-y-6">
      {/* Security Status */}
      <div className={`rounded-lg p-6 border ${
        isLocked ? 'bg-red-500/10 border-red-500/30' :
        security?.two_fa_enabled ? 'bg-green-500/10 border-green-500/30' :
        'bg-yellow-500/10 border-yellow-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">Estado de Seguridad</h3>
            <div className="flex gap-3">
              {isLocked && (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium border border-red-500/30">
                  🔒 Cuenta Bloqueada
                </span>
              )}
              {security?.two_fa_enabled ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium border border-green-500/30">
                  ✅ 2FA Activo
                </span>
              ) : (
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium border border-yellow-500/30">
                  ⚠️ 2FA Desactivado
                </span>
              )}
              {security?.email_verified ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                  ✓ Email Verificado
                </span>
              ) : (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                  ✗ Email Sin Verificar
                </span>
              )}
            </div>
          </div>
          <div className="text-5xl">
            {isLocked ? '🔒' : security?.two_fa_enabled ? '🛡️' : '⚠️'}
          </div>
        </div>
      </div>

      {/* Security Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Sesiones Activas</div>
          <div className="text-2xl font-bold text-white">{activeSessions.length}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Intentos Fallidos</div>
          <div className={`text-2xl font-bold ${failedAttempts > 3 ? 'text-red-400' : 'text-white'}`}>
            {failedAttempts}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Último Login</div>
          <div className="text-sm font-semibold text-white">
            {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('es-ES') : 'Nunca'}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-[#1a4d5c]/30">
          <div className="text-xs text-gray-400 mb-1">Contraseña</div>
          <div className="text-sm font-semibold text-white">
            {security?.password_updated_at ? 
              new Date(security.password_updated_at).toLocaleDateString('es-ES') : 
              'Original'}
          </div>
        </div>
      </div>

      {/* 2FA Configuration */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-white">Autenticación de Dos Factores (2FA)</h4>
            <p className="text-sm text-gray-400 mt-1">
              {security?.two_fa_enabled ? 
                'La autenticación de dos factores está activa para esta cuenta' :
                'La cuenta no tiene 2FA activado - menos seguro'}
            </p>
          </div>
          <button
            onClick={handleToggle2FA}
            disabled={isProcessing}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              security?.two_fa_enabled ?
                'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50' :
                'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50'
            }`}
          >
            {security?.two_fa_enabled ? 'Deshabilitar 2FA' : 'Habilitar 2FA'}
          </button>
        </div>
        {security?.two_fa_method && (
          <div className="text-sm text-gray-400">
            Método: <span className="text-white">{security.two_fa_method}</span>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Sesiones Activas ({activeSessions.length})</h4>
          {activeSessions.length > 0 && (
            <button
              onClick={handleRevokeAllSessions}
              disabled={isProcessing}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors border border-red-500/50 disabled:opacity-50 text-sm"
            >
              🚫 Revocar Todas
            </button>
          )}
        </div>
        {activeSessions.length > 0 ? (
          <div className="space-y-3">
            {activeSessions.map((session, index) => (
              <div key={index} className="p-4 bg-black/50 rounded-lg border border-[#1a4d5c]/30">
                <div className="flex items-start justify-between">
                  <div className="flex gap-3">
                    <div className="text-3xl">
                      {session.device?.toLowerCase().includes('mobile') ? '📱' :
                       session.device?.toLowerCase().includes('tablet') ? '📱' :
                       '💻'}
                    </div>
                    <div>
                      <div className="text-white font-medium">{session.device || 'Desconocido'}</div>
                      <div className="text-sm text-gray-400">IP: {session.ip_address || 'N/A'}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Último acceso: {new Date(session.last_active_at || session.created_at).toLocaleString('es-ES')}
                      </div>
                      {session.location && (
                        <div className="text-xs text-gray-500">
                          📍 {session.location}
                        </div>
                      )}
                    </div>
                  </div>
                  {session.is_current && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                      Actual
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No hay sesiones activas</p>
        )}
      </div>

      {/* Failed Login Attempts */}
      {failedAttempts > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-red-400 font-semibold mb-1">⚠️ Intentos de Login Fallidos</h4>
              <p className="text-sm text-red-300">
                Esta cuenta tiene {failedAttempts} intento(s) de login fallidos recientes
              </p>
            </div>
            <button
              onClick={handleResetLoginAttempts}
              disabled={isProcessing}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors disabled:opacity-50 text-sm"
            >
              Resetear
            </button>
          </div>
          {security?.last_failed_login_at && (
            <div className="text-xs text-red-300">
              Último intento fallido: {new Date(security.last_failed_login_at).toLocaleString('es-ES')}
            </div>
          )}
        </div>
      )}

      {/* Role & Permissions */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <h4 className="font-semibold text-white mb-4">Rol & Permisos</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-400 mb-1">Rol Principal</div>
            <div className="px-3 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg inline-block font-medium">
              {user.role || 'student'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-400 mb-1">Fecha de Registro</div>
            <div className="text-white font-medium">
              {new Date(user.created_at).toLocaleDateString('es-ES')}
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-black/50 rounded-lg">
          <div className="text-xs text-gray-400 mb-2">Permisos</div>
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">👁️ Ver Contenido</span>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">📝 Enviar Proyectos</span>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">💬 Acceso Comunidad</span>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">🎥 Ver Sesiones</span>
          </div>
        </div>
      </div>

      {/* Security Actions */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <h4 className="font-semibold text-white mb-4">Acciones de Seguridad</h4>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleRevokeAllSessions}
            disabled={isProcessing}
            className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            🚫 Cerrar Todas las Sesiones
          </button>
          <button
            onClick={handleToggle2FA}
            disabled={isProcessing}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            🔐 {security?.two_fa_enabled ? 'Deshabilitar' : 'Habilitar'} 2FA
          </button>
          {failedAttempts > 0 && (
            <button
              onClick={handleResetLoginAttempts}
              disabled={isProcessing}
              className="px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              🔄 Resetear Intentos Fallidos
            </button>
          )}
          {isLocked && (
            <button
              onClick={() => alert('Función de desbloqueo próximamente')}
              className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium"
            >
              🔓 Desbloquear Cuenta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}